import { backendApi } from "@/services/backend/api";
import {
  CreatedRes,
  CreatePestReq,
  EmptyRes,
  PestGetReq,
  PestList,
  PestListReq,
  PestPutSynonymsReq,
  PestRenameReq,
  PestUpdateAbbreviationReq,
  PestUpdateDescriptionReq,
  PestUpdateTechnicalTermReq,
  PestView,
} from "@/services/backend/qpm/pests/types/pest";

const qpmPestServiceApi = backendApi
  .enhanceEndpoints({
    addTagTypes: [
      "QpmPests",
      "QpmPest", // single
      "QpmPestCategory",
      "QpmPestType",
      "QpmPestCategoryRecord",
      "QpmPestTypeRecord",
    ],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      qpmPestCreate: build.mutation<CreatedRes, CreatePestReq>({
        query: (request) => ({
          url: `/qpm/pests`,
          method: "POST",
          body: request,
        }),
        invalidatesTags: ["QpmPests"],
      }),
      qpmPestList: build.query<PestList, PestListReq>({
        query: (request) => ({
          url: `/qpm/pests/list`,
          method: "GET",
          params: request,
        }),
        providesTags: (result) =>
          result
            ? [
                { type: "QpmPests", id: "LIST" },
                ...result.items.map((p) => ({
                  type: "QpmPest" as const,
                  id: p.id,
                })),
              ]
            : [{ type: "QpmPests", id: "LIST" }],
      }),

      qpmPestGet: build.query<PestView, PestGetReq>({
        query: (params) => ({
          url: `/qpm/pests/get`,
          method: "GET",
          params,
        }),
        providesTags: (result) =>
          result ? [{ type: "QpmPest", id: result.id }] : [],
      }),

      qpmPestRename: build.mutation<EmptyRes, PestRenameReq>({
        query: (body) => ({
          url: `/qpm/pests/name`,
          method: "PATCH",
          body,
        }),
        invalidatesTags: (_r, _e, b) => [
          { type: "QpmPest", id: b.id },
          { type: "QpmPests", id: "LIST" },
        ],
      }),

      // new update commands
      qpmPestUpdateAbbreviation: build.mutation<
        EmptyRes,
        PestUpdateAbbreviationReq
      >({
        query: (body) => ({
          url: `/qpm/pests/abbreviation`,
          method: "PATCH",
          body,
        }),
        invalidatesTags: (_r, _e, b) => [
          { type: "QpmPest", id: b.id },
          { type: "QpmPests", id: "LIST" },
        ],
      }),

      qpmPestUpdateDescription: build.mutation<
        EmptyRes,
        PestUpdateDescriptionReq
      >({
        query: (body) => ({
          url: `/qpm/pests/description`,
          method: "PATCH",
          body,
        }),
        invalidatesTags: (_r, _e, b) => [
          { type: "QpmPest", id: b.id },
          { type: "QpmPests", id: "LIST" },
        ],
      }),

      qpmPestUpdateTechnicalTerm: build.mutation<
        EmptyRes,
        PestUpdateTechnicalTermReq
      >({
        query: (body) => ({
          url: `/qpm/pests/technical-term`,
          method: "PATCH",
          body,
        }),
        invalidatesTags: (_r, _e, b) => [
          { type: "QpmPest", id: b.id },
          { type: "QpmPests", id: "LIST" },
        ],
      }),

      qpmPestPutSynonyms: build.mutation<EmptyRes, PestPutSynonymsReq>({
        query: (body) => ({
          url: `/qpm/pests/synonyms`,
          method: "PUT",
          body,
        }),
        invalidatesTags: (_r, _e, b) => [
          { type: "QpmPest", id: b.id },
          { type: "QpmPests", id: "LIST" },
        ],
      }),
    }),
  });

export const {
  useQpmPestCreateMutation,
  useQpmPestListQuery,
  useQpmPestGetQuery,
  useQpmPestRenameMutation,
  useQpmPestUpdateAbbreviationMutation,
  useQpmPestUpdateDescriptionMutation,
  useQpmPestUpdateTechnicalTermMutation,
  useQpmPestPutSynonymsMutation,
} = qpmPestServiceApi;
